import { z } from 'zod';

// Common validation patterns
const patterns = {
  name: /^[a-zA-Z0-9\s._-]+$/,
  serverName: /^[a-zA-Z][a-zA-Z0-9_-]*$/,
  uuid: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
};

// Chat message validation
export const chatMessageSchema = z.object({
  id: z.string(),
  role: z.enum(['user', 'assistant', 'system', 'tool']),
  content: z.string().max(65536, 'Message is too long'),
  timestamp: z.string().datetime().or(z.date()),
}).refine((data) => {
  // Allow empty content for assistant messages (streaming placeholders)
  if (data.role === 'assistant') {
    return true;
  }
  // Require content for other message types
  return data.content && data.content.trim().length > 0;
}, {
  message: 'Message cannot be empty',
  path: ['content']
});

// Chat settings validation
export const chatSettingsSchema = z.object({
  provider: z.string().optional(),
  model: z.string().optional(),
  temperature: z.number()
    .min(0, 'Temperature must be at least 0')
    .max(2, 'Temperature must be at most 2')
    .optional(),
  maxTokens: z.number()
    .min(1, 'Max tokens must be at least 1')
    .max(32000, 'Max tokens must be at most 32000')
    .optional(),
  domain: z.enum(['coding', 'general', 'reasoning', 'retrieval', 'analytics']).optional(),
  costSensitivity: z.enum(['low', 'medium', 'high']).optional(),
  dataSensitivity: z.enum(['public', 'internal', 'restricted']).optional(),
  aiName: z.string().optional(),
});

// Saved state validation
export const savedStateSchema = z.object({
  name: z.string()
    .min(1, 'Name is required')
    .max(100, 'Name is too long')
    .regex(patterns.name, 'Name contains invalid characters'),
  description: z.string()
    .max(500, 'Description is too long')
    .optional(),
  workspaceId: z.string()
    .uuid('Invalid workspace ID format'),
});

export const createSavedStateSchema = savedStateSchema.extend({
  stateData: z.record(z.unknown()).default({}),
  sessionId: z.string().uuid().optional(),
  chatMessages: z.array(chatMessageSchema).optional(),
  chatSettings: chatSettingsSchema.optional(),
  autoOpenOnStartup: z.boolean().optional(),
});

export const updateSavedStateSchema = savedStateSchema.partial().extend({
  stateData: z.record(z.unknown()).optional(),
  sessionId: z.string().uuid().optional(),
  chatMessages: z.array(chatMessageSchema).optional(),
  chatSettings: chatSettingsSchema.optional(),
  autoOpenOnStartup: z.boolean().optional(),
});

// Workspace validation
export const workspaceSettingsSchema = z.object({
  maxTokens: z.number()
    .min(1, 'Max tokens must be at least 1')
    .max(32000, 'Max tokens must be at most 32000')
    .optional(),
  temperature: z.number()
    .min(0, 'Temperature must be at least 0')
    .max(2, 'Temperature must be at most 2')
    .optional(),
  domain: z.enum(['coding', 'general', 'reasoning', 'retrieval', 'analytics']).optional(),
  costSensitivity: z.enum(['low', 'medium', 'high']).optional(),
  dataSensitivity: z.enum(['public', 'internal', 'restricted']).optional(),
});

export const workspaceSchema = z.object({
  name: z.string()
    .min(1, 'Name is required')
    .max(100, 'Name is too long')
    .regex(patterns.name, 'Name contains invalid characters'),
  description: z.string()
    .max(500, 'Description is too long')
    .optional(),
  contextPrompt: z.string()
    .max(2000, 'Context prompt is too long')
    .optional(),
  allowedMcpServers: z.array(
    z.string().regex(patterns.serverName, 'Invalid server name format')
  ).default([]),
  allowedProviders: z.array(
    z.string().regex(patterns.serverName, 'Invalid provider name format')
  ).default([]),
  settings: workspaceSettingsSchema.default({}),
});

// Validation utility functions
export class ValidationError extends Error {
  constructor(
    message: string,
    public field: string,
    public code: string
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}

export function validateInput<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  fieldName?: string
): T {
  try {
    return schema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const firstError = error.errors[0];
      throw new ValidationError(
        firstError.message,
        fieldName || firstError.path.join('.'),
        firstError.code
      );
    }
    throw error;
  }
}

export function validateInputSafe<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: string[] } {
  try {
    const validated = schema.parse(data);
    return { success: true, data: validated };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.map(err => 
        `${err.path.join('.')}: ${err.message}`
      );
      return { success: false, errors };
    }
    return { success: false, errors: ['Validation failed'] };
  }
}

// Input sanitization for XSS prevention
export function sanitizeInput(input: string): string {
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
}

// File name sanitization
export function sanitizeFileName(fileName: string): string {
  return fileName
    .replace(/[<>:"/\\|?*]/g, '-') // Replace invalid chars with dash
    .replace(/\s+/g, '_') // Replace spaces with underscore
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
    .substring(0, 255); // Limit length
}

// URL validation
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// Email validation (basic)
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// UUID validation
export function isValidUUID(uuid: string): boolean {
  return patterns.uuid.test(uuid);
}

// Validation hook for React components
export function useValidation<T>(
  schema: z.ZodSchema<T>
) {
  const [errors, setErrors] = React.useState<string[]>([]);
  
  const validate = React.useCallback((data: unknown): T | null => {
    const result = validateInputSafe(schema, data);
    if (result.success) {
      setErrors([]);
      return result.data;
    } else {
      setErrors(result.errors);
      return null;
    }
  }, [schema]);
  
  const clearErrors = React.useCallback(() => {
    setErrors([]);
  }, []);
  
  return { validate, errors, clearErrors, hasErrors: errors.length > 0 };
}

// React import for the hook
import React from 'react';